import type {CSSResultArray} from 'lit';
import {html} from 'lit';
import {customElement, property, query, state} from 'lit/decorators.js';
import type {FormFieldSchema} from '#lit/components/abstract/form_field/index.js';
import FlyFormField, {
  ActionTag,
  FormFieldValueTypeRepr,
  FORM_FIELD_STATUS,
} from '#lit/components/abstract/form_field/index.js';
import styles from './style.scss';
import {isNonEmptyString} from '#lit/utils/index.js';
import {classMap} from 'lit/directives/class-map.js';
import {styleMap} from 'lit/directives/style-map.js';
import {repeat} from 'lit/directives/repeat.js';
import type {CustomSelectEvent} from '#lit/components/base/select/index.js';
import type FlySelect from '#lit/components/base/select/index.js';
import {COMPONENT_TAG as FLY_SELECT_COMPONENT_TAG} from '#lit/components/base/select/index.js';
import type FlyMultiSelect from '#lit/components/base/multiselect/index.js';
import {COMPONENT_TAG as FLY_MULTISELECT_COMPONENT_TAG} from '#lit/components/base/multiselect/index.js';
import {ref} from 'lit/directives/ref.js';
import {watch} from '#lit/utils/dom.js';
import {when} from 'lit/directives/when.js';

export const COMPONENT_TAG = 'fly-form-select-field';

const FIELD_NODE_TYPE = {
  CUSTOM_SELECT: FLY_SELECT_COMPONENT_TAG,
  CUSTOM_MULTISELECT: FLY_MULTISELECT_COMPONENT_TAG,
  SELECT: 'select',
} as const;

type FieldNodeType = (typeof FIELD_NODE_TYPE)[keyof typeof FIELD_NODE_TYPE];

export interface FormSelectFieldOption {
  value: string;
  label: string;
  labelDescription?: string;
  disabled?: boolean;
}

@customElement(COMPONENT_TAG)
export default class FlyFormSelectField extends FlyFormField {
  @property({type: String})
  name = '';

  @property({type: String})
  label = '';

  @property({type: String})
  hint? = '';

  @property({type: Boolean})
  required = false;

  @state()
  schema: FormFieldSchema | null = null;

  @property({type: Array})
  initial: string[] = [];

  @property({type: Array})
  value: string[] = [];

  @property({type: Boolean})
  native = false;

  @property({type: Boolean})
  multi = false;

  @property({type: Boolean})
  searchable = false;

  @property({type: Boolean})
  loading = false;

  @property({type: Number})
  maxsize!: number;

  @property({type: Number})
  minsize!: number;

  @property({type: Array})
  options: FormSelectFieldOption[] = [];

  @property({type: String})
  width!: string;

  @query('fly-select')
  $flySelect?: FlySelect;

  @state()
  fieldNodeType: FieldNodeType = FIELD_NODE_TYPE.CUSTOM_SELECT;

  $fieldNode!: HTMLSelectElement | FlySelect | FlyMultiSelect;

  static get styles(): CSSResultArray {
    return [styles];
  }

  get optionValues() {
    return Array.from(
      new Set<string>(
        this.options
          .map(option => (isNonEmptyString(option) ? option : option.value))
          .filter(isNonEmptyString)
      )
    );
  }

  firstUpdated() {
    this.resetState();
    this.resetInitialValue();
  }

  @watch('options')
  updateSchema() {
    this.schema = {
      type: FormFieldValueTypeRepr.STRING_ARRAY,
      minsize: this.multi
        ? Math.max(this.minsize, this.required ? 1 : 0)
        : this.required
          ? 1
          : 0,
      maxsize: this.multi ? Math.max(this.maxsize, 1) : 1,
      options: this.optionValues,
    };
  }

  registerNode($node: Element | undefined) {
    if (!$node) return;
    const nodeTagName = $node.tagName.toLowerCase();
    switch (nodeTagName) {
      case FIELD_NODE_TYPE.SELECT: {
        this.$fieldNode = $node as HTMLSelectElement;
        this.fieldNodeType = FIELD_NODE_TYPE.SELECT;
        break;
      }

      case FIELD_NODE_TYPE.CUSTOM_SELECT: {
        this.$fieldNode = $node as FlySelect;
        this.fieldNodeType = FIELD_NODE_TYPE.CUSTOM_SELECT;
        break;
      }

      case FIELD_NODE_TYPE.CUSTOM_MULTISELECT: {
        this.$fieldNode = $node as FlyMultiSelect;
        this.fieldNodeType = FIELD_NODE_TYPE.CUSTOM_MULTISELECT;
        break;
      }
    }
  }

  reset() {
    super.reset();
    if (!this.$fieldNode) return;
    switch (this.fieldNodeType) {
      case FIELD_NODE_TYPE.CUSTOM_SELECT: {
        const $fieldNode = this.$fieldNode as FlySelect;
        $fieldNode.initialize();
        break;
      }

      case FIELD_NODE_TYPE.CUSTOM_MULTISELECT: {
        const $fieldNode = this.$fieldNode as FlyMultiSelect;
        $fieldNode.reset();
        break;
      }
    }
  }

  defaultSelectMarkup() {
    if (!this.schema) return html``;
    const classes = {
      'fly-form__field--select': true,
      'fly-form__field--select--native': this.native,
      'fly-form__field--active': this.active,
      'fly-form__field--select--active': this.active,
      'fly-form__field--disabled': this.disabled,
      'fly-form__field--select--disabled': this.disabled,
      'fly-form__field--error': Boolean(this.errors.length),
      'fly-form__field--select--error': Boolean(this.errors.length),
      'fly-form__field--valid': this.errors.length === 0,
      'fly-form__field--select--valid': this.errors.length === 0,
    };

    const styles = {
      '--fly-select-width': isNonEmptyString(this.width)
        ? this.width.trim()
        : '100%',
    };

    return html`
      <select
        ${ref(this.registerNode.bind(this))}
        ?multi=${this.multi}
        @focus=${() => {
          this.action({
            tag: ActionTag.UPDATE_STATUS,
            value: [
              [FORM_FIELD_STATUS.ACTIVE, true],
              [FORM_FIELD_STATUS.VISITED, true],
            ],
          });
        }}
        @change=${(evt: Event) => {
          this.action({
            tag: ActionTag.UPDATE_VALUE,
            value: [(evt.composedPath()[0] as HTMLSelectElement).value],
          });
        }}
        @blur=${(evt: Event) => {
          this.action({
            tag: ActionTag.UPDATE_STATUS,
            value: [
              [FORM_FIELD_STATUS.ACTIVE, false],
              [FORM_FIELD_STATUS.TOUCHED, true],
            ],
          });

          this.action({
            tag: ActionTag.UPDATE_VALUE,
            value: [(evt.composedPath()[0] as HTMLSelectElement).value],
          });
        }}
        name=${isNonEmptyString(this.name) ? this.name : this.key}
        class=${classMap(classes)}
        style=${styleMap(styles)}
        ?required=${this.required}
        ?disabled=${this.disabled}
        ?readonly=${this.readonly}
      >
        <option
          value=${this.placeholder}
          ?selected="${!Array.isArray(this.value) || this.value.length === 0}"
        >
          ${this.placeholder}
        </option>
        ${repeat(
          this.options,
          (_, idx: number) => `repeatkey:${COMPONENT_TAG}-${this.key}-${idx}`,
          (option: FormSelectFieldOption) => {
            const value = isNonEmptyString(option) ? option : option.value;
            const label = isNonEmptyString(option) ? option : option.label;
            const selected =
              Array.isArray(this.value) &&
              this.value.find(selectedOption => selectedOption === value);
            return html`
              <option value=${value} ?selected=${selected}>${label}</option>
            `;
          }
        )}
      </select>
    `;
  }

  customSelectMarkup() {
    if (!this.schema) return html``;
    const styles = {
      '--fly-select-width': isNonEmptyString(this.width)
        ? this.width.trim()
        : '100%',
    };

    const loadingIndicator = () => {
      const classes = {
        'fly-form-select-field__menu__loading-indicator': true,
        'fly-form-select-field__menu__loading-indicator--active': this.loading,
      };

      return html`
        <div class=${classMap(classes)}>
          <fly-loader loading></fly-loader>
        </div>
      `;
    };

    const options = () => {
      return html`
        ${repeat(
          this.options,
          (_, idx: number) => `repeatkey:${COMPONENT_TAG}-${this.key}-${idx}`,
          (option: FormSelectFieldOption) => {
            const value = isNonEmptyString(option) ? option : option.value;
            const label = isNonEmptyString(option) ? option : option.label;
            const description = option.labelDescription;
            return html`
              <fly-list-item value=${value} label=${label}>
                ${label}
                ${when(
                  this.hint,
                  () => html`
                    <fly-tooltip content="${this.hint!}">
                      <fly-icon .icon=${'info_outlined'}></fly-icon>
                    </fly-tooltip>
                  `
                )}
                ${description
                  ? html` <div class="fly-form-select-field__description">
                      ${description}
                    </div>`
                  : html``}
              </fly-list-item>
            `;
          }
        )}
      `;
    };

    return html`
      <fly-select
        ${ref(this.registerNode.bind(this))}
        key=${this.key}
        ?formfield=${true}
        initial=${this.initial[0] || ''}
        ?searchable=${this.searchable}
        ?formfieldErrors=${Boolean(this.errors.length)}
        placeholder=${this.placeholder}
        @focus=${() => {
          this.action({
            tag: ActionTag.UPDATE_STATUS,
            value: [
              [FORM_FIELD_STATUS.ACTIVE, true],
              [FORM_FIELD_STATUS.VISITED, true],
              [FORM_FIELD_STATUS.TOUCHED, true],
            ],
          });
        }}
        @selected=${(e: CustomEvent<CustomSelectEvent>) => {
          this.action({
            tag: ActionTag.UPDATE_VALUE,
            value: isNonEmptyString(e.detail.value) ? [e.detail.value] : [],
          });
        }}
        @blur=${() => {
          this.action({
            tag: ActionTag.UPDATE_STATUS,
            value: [
              [FORM_FIELD_STATUS.ACTIVE, false],
              [FORM_FIELD_STATUS.TOUCHED, true],
            ],
          });
        }}
        name=${isNonEmptyString(this.name) ? this.name : this.key}
        style=${styleMap(styles)}
        ?required=${this.required}
        ?disabled=${this.disabled}
        ?readonly=${this.readonly}
      >
        <slot></slot>
        ${this.loading ? loadingIndicator() : options()}
        <slot name="after-options"></slot>
      </fly-select>
    `;
  }

  //TODO: to be completed later
  customMultiSelectMarkup() {
    if (!this.schema) return html``;
    return html``;
  }

  fieldMarkup() {
    if (!this.schema) return html``;

    if (this.multi && !this.native) {
      return this.customMultiSelectMarkup();
    }

    if (!this.native) {
      return this.customSelectMarkup();
    }

    return this.defaultSelectMarkup();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: FlyFormSelectField;
  }
}
